import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCities } from '../services/citiesService';
import {
  Typography,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Alert,
  Spin,
  Select,
  InputNumber,
  TimePicker,
  Upload,
  message,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { uploadImage } from '../services/uploadService';
import { EditOutlined, DeleteOutlined, PlusOutlined, OrderedListOutlined } from '@ant-design/icons';
import { createTour, deleteTour, getTours, updateTour } from '../services/toursService';
import { render } from '@testing-library/react';

function ToursAdmin() {
  const navigate = useNavigate();
  const [tours, setTours] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTour, setEditingTour] = useState(null);
  const [form] = Form.useForm();
  const { Title } = Typography;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [toursData, citiesData] = await Promise.all([getTours(), getCities()]);
        setTours(toursData);
        setCities(citiesData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const fetchTours = async () => {
    try {
      const data = await getTours();
      setTours(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (tour = null) => {
    if (tour) {
      setEditingTour(tour);
      form.setFieldsValue({
        name: tour.name,
        description: tour.description || '',
        duration: tour.duration,
        distance: tour.distance,
        type: tour.type,
        difficulty: tour.difficulty,
        image_url: tour.image_url || '',
        city_id: tour.city_id,
        visible: tour.visible,
      });
    } else {
      setEditingTour(null);
      form.resetFields();
    }
    setOpenDialog(true);
  };

  const handleCloseModal = () => {
    setOpenDialog(false);
    setEditingTour(null);
    form.resetFields();
  };

  const handleSubmit = async values => {
    setLoading(true);
    try {
      if (editingTour) {
        await updateTour(editingTour.id, values);
      } else {
        await createTour(values);
      }
      fetchTours();
      handleCloseModal();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async id => {
    Modal.confirm({
      title: 'Are you sure you want to delete this tour?',
      onOk: async () => {
        setLoading(true);
        try {
          await deleteTour(id);
          fetchTours();
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Visible',
      dataIndex: 'visible',
      key: 'visible',
      render: visible => (visible ? 'Yes' : 'No'),
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
    },
    {
      title: 'Distance (miles)',
      dataIndex: 'distance',
      key: 'distance',
    },
    {
      title: 'Difficulty',
      dataIndex: 'difficulty',
      key: 'difficulty',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => handleOpenModal(record)} />
          <Button
            type="text"
            icon={<OrderedListOutlined />}
            onClick={() => navigate(`/admin/tours/${record.id}/stops`)}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  if (loading)
    return (
      <div style={{ textAlign: 'center', padding: '24px' }}>
        <Spin size="large" />
      </div>
    );
  if (error) return <Alert message={error} type="error" />;

  return (
    <div style={{ padding: '24px' }}>
      <div
        style={{
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Title level={2}>Tours</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal()}>
          Add Tour
        </Button>
      </div>

      <Table columns={columns} dataSource={tours} rowKey="id" />

      <Modal
        title={editingTour ? 'Edit Tour' : 'Add Tour'}
        open={openDialog}
        onCancel={handleCloseModal}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please input the tour name!' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item
            name="city_id"
            label="City"
            rules={[{ required: true, message: 'Please select a city!' }]}
          >
            <Select>
              {cities.map(city => (
                <Select.Option key={city.id} value={city.id}>
                  {city.name}, {city.state}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="duration" label="Duration (minutes)">
            <InputNumber min={1} />
          </Form.Item>
          <Form.Item name="distance" label="Distance (miles)">
            <InputNumber min={0} step={0.1} />
          </Form.Item>
          <Form.Item
            name="type"
            label="Type"
            rules={[{ required: true, message: 'Please select a tour type!' }]}
          >
            <Select>
              <Select.Option value="walking">Walking</Select.Option>
              <Select.Option value="cycling">Cycling</Select.Option>
              <Select.Option value="driving">Driving</Select.Option>
              <Select.Option value="mixed">Mixed</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="difficulty" label="Difficulty">
            <InputNumber min={1} max={5} />
          </Form.Item>
          <Form.Item
            name="visible"
            label="Visible"
            rules={[{ required: true, message: 'Please select visibility!' }]}
          >
            <Select>
              <Select.Option value={true}>Yes</Select.Option>
              <Select.Option value={false}>No</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="image_url" label="Image">
            <Upload
              maxCount={1}
              accept="image/*"
              beforeUpload={async file => {
                try {
                  const url = await uploadImage(file);
                  form.setFieldsValue({ image_url: url });
                  message.success('Image uploaded successfully');
                } catch (err) {
                  message.error('Failed to upload image');
                }
                return false;
              }}
            >
              <Button icon={<UploadOutlined />}>Upload Image</Button>
            </Upload>
            {form.getFieldValue('image_url') && (
              <img
                src={form.getFieldValue('image_url')}
                alt="Preview"
                style={{
                  marginTop: 8,
                  maxWidth: '100%',
                  maxHeight: 200,
                  objectFit: 'contain',
                }}
              />
            )}
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCloseModal}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingTour ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default ToursAdmin;
